/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";

interface MultiSelectDropdownProps {
  label?: string;
  placeholder?: string;
  selectedValues?: any[];
  onSelect: (values: any[]) => void;
  options: any[];
  onSearchChange?: (term: string) => void;
  name?: string;
  required?: boolean;
  error?: string;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label,
  placeholder,
  selectedValues,
  onSelect,
  options,
  onSearchChange,
  name,
  required,
  error,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 🔥 Remove selected items from the dropdown
  const filteredOptions = options?.filter(
    (opt) => !selectedValues?.some((sel) => sel.value === opt.value)
  );

  const handleSelect = (option: any) => {
    const newSelection = [...(selectedValues || []), option];
    onSelect(newSelection);
  };

  const handleRemove = (option: any) => {
    const filtered: any = selectedValues?.filter(
      (val) => val.value !== option.value
    );
    onSelect(filtered);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (onSearchChange) {
      const debounce = setTimeout(() => {
        onSearchChange(searchTerm);
      }, 500);
      return () => clearTimeout(debounce);
    }
  }, [searchTerm]);

  return (
    <div ref={dropdownRef} className="relative w-full mb-4 mt-2">
      {label && (
        <label className="block text-sm font-medium mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      {/* Selected Tags */}
      <div
        className={`min-h-[42px] border rounded-md p-2 flex flex-wrap items-center gap-2 cursor-pointer ${
          isOpen && "border-primary"
        } ${error ? "border-red-500" : "border-gray-300"}`}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {selectedValues?.length === 0 ? (
          <span className="text-gray-400 text-sm">{placeholder}</span>
        ) : (
          selectedValues?.map((val, idx) => (
            <div
              key={idx}
              className="flex items-center bg-blue-100 text-sm px-2 py-1 rounded-full"
            >
              {val.label}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(val);
                }}
                className="ml-2 text-blue-500 hover:text-blue-700"
              >
                &times;
              </button>
            </div>
          ))
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute bg-white border border-gray-300 shadow-lg rounded-md mt-1 z-20 w-full max-h-[300px] overflow-y-auto">
          <div className="p-2">
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              autoFocus
            />
          </div>

          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, idx) => (
              <div
                key={idx}
                onClick={() => handleSelect(option)}
                className="px-4 py-2 cursor-pointer text-sm hover:bg-gray-100"
              >
                {option.label}
              </div>
            ))
          ) : (
            <p className="text-sm text-center py-2 text-gray-500">No results</p>
          )}
        </div>
      )}

      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

export default MultiSelectDropdown;
